/**
 * Admin-Detailseite für einen einzelnen Mandanten – zeigt alle Benutzer.
 * Erreichbar unter /admin/organisations/:orgId/* (erfordert admin:write oder
 * organisation:write für die eigene Organisation).
 */
import React, { useState } from "react";
import { Box, Container } from "@mui/material";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Bolt as BoltIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import PageTemplate from "../../layout/PageTemplate";
import { SeitenTitelContext } from "../../layout/SeitenTitelContext";
import Sidebar, {
  SIDEBAR_WIDTH,
  SidebarFlatItem,
  SidebarSegment,
} from "../../layout/Sidebar";
import OrgMembersSection from "./sections/OrgMembersSection";
import InviteUserDialog from "./sections/InviteUserDialog";
import MemberDetailDialog from "./sections/MemberDetailDialog";
import GesellschaftSection from "./sections/GesellschaftSection";
import AbzuegeSection from "./sections/AbzuegeSection";
import InvestmentskostenSection from "./sections/InvestmentskostenSection";
import BetriebskostenSection from "./sections/BetriebskostenSection";
import WirtschaftlichkeitScoreSection from "./sections/WirtschaftlichkeitScoreSection";
import ReifegradScoreSection from "./sections/ReifegradScoreSection";
import { useAdminOrgDetail } from "./hooks/useAdminOrgDetail";
import { useHeaderConfig } from "../../../contexts/HeaderContext";

const items: SidebarFlatItem[] = [
  { label: "Benutzer", icon: PeopleIcon, index: 0, key: "users" },
  {
    label: "Gesellschaft",
    icon: BusinessIcon,
    index: 1,
    key: "gesellschaft",
  },
  { label: "Erträge", icon: BoltIcon, index: 2, key: "ertraege" },
  { label: "Kosten", icon: PaymentIcon, index: 3, key: "kosten" },
  { label: "Score", icon: AssessmentIcon, index: 4, key: "score" },
];

const segments: SidebarSegment[] = [{ items }];

const AdminOrgDetailPage: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { org, members, roles, loading, error, reload } = useAdminOrgDetail(
    orgId ?? "",
  );
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // Zurück zur Startseite führt das Logo im Header.
  useHeaderConfig(org?.display_name ?? null);

  const currentItem =
    items.find((item) => location.pathname.endsWith(`/${item.key}`)) ??
    items[0];

  const handleTabChange = (tabIndex: number) => {
    const item = items.find((i) => i.index === tabIndex);
    if (item) navigate(`/admin/organisations/${orgId}/${item.key}`);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar
        segments={segments}
        currentTab={currentItem.index}
        onTabChange={handleTabChange}
      />

      <Box
        sx={{
          flex: 1,
          marginLeft: `${SIDEBAR_WIDTH}px`,
          width: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
          maxWidth: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
        }}
      >
        {/* Der Mandant steht im Header über der Sidebar, der Titel – die Seite aus der
            Sidebar – über dem Inhalt (PageTemplate bringt ihn dorthin). */}
        <Container maxWidth="xl">
          <SeitenTitelContext.Provider
            value={{ titel: currentItem.label }}
          >
            <PageTemplate isLoading={loading} error={error}>
              <Routes>
                <Route index element={<Navigate to="users" replace />} />

                <Route
                  path="users"
                  element={
                    <>
                      <OrgMembersSection
                        members={members}
                        onInviteClick={() => setInviteDialogOpen(true)}
                        onMemberClick={(userId) => setSelectedUserId(userId)}
                      />
                      <InviteUserDialog
                        open={inviteDialogOpen}
                        orgId={orgId ?? ""}
                        roles={roles}
                        onClose={() => setInviteDialogOpen(false)}
                        onInvited={reload}
                      />
                      <MemberDetailDialog
                        open={selectedUserId !== null}
                        orgId={orgId ?? ""}
                        userId={selectedUserId}
                        onClose={() => setSelectedUserId(null)}
                      />
                    </>
                  }
                />

                <Route
                  path="gesellschaft"
                  element={<GesellschaftSection orgId={orgId ?? ""} />}
                />

                <Route
                  path="ertraege"
                  element={<AbzuegeSection orgId={orgId ?? ""} />}
                />

                <Route
                  path="kosten"
                  element={
                    <>
                      <InvestmentskostenSection orgId={orgId ?? ""} />
                      <BetriebskostenSection orgId={orgId ?? ""} />
                    </>
                  }
                />

                <Route
                  path="score"
                  element={
                    <>
                      <ReifegradScoreSection orgId={orgId ?? ""} />
                      <WirtschaftlichkeitScoreSection orgId={orgId ?? ""} />
                    </>
                  }
                />

                <Route path="*" element={<Navigate to="users" replace />} />
              </Routes>
            </PageTemplate>
          </SeitenTitelContext.Provider>
        </Container>
      </Box>
    </Box>
  );
};

export default AdminOrgDetailPage;
